import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Text, Button, Card, Heading, Spinner } from 'frosted-ui';
import UpgradeDialog from '../components/billing/UpgradeDialog';
import { needsProForNewTest } from '../components/billing/plan';
import { usePlan } from '../hooks/usePlan';
import { ArrowLeft16, ArrowRight16 } from '@frosted-ui/icons';
import StepWhatToTest from '../components/wizard/StepWhatToTest';
import StepPlaceLink from '../components/wizard/StepPlaceLink';
import StepPlacePartners, { cleanUsername, isValidUsername, USERNAME_RULE } from '../components/wizard/StepPlacePartners';
import ProblemCallout, { type Problem } from '../components/ProblemCallout';
import LoadFailed from '../components/LoadFailed';
import { priceTestProblem } from '../components/wizard/priceTest';
import StepPlaceInWhop, { MemberOfferPreview } from '../components/wizard/StepPlaceInWhop';
import StepGoLive from '../components/wizard/StepGoLive';
import FirstVisitChecklist from '../components/wizard/FirstVisitChecklist';
import DeleteDraftButton from '../components/wizard/DeleteDraftButton';
import { IN_WHOP_KEY, planResume } from '../components/wizard/resumeDraft';
import TestSummaryAside from '../components/wizard/TestSummaryAside';
import OfferTypeChoice from '../components/wizard/OfferTypeChoice';
import StepWhatToSell from '../components/wizard/StepWhatToSell';
import {
  createUnlockTest,
  UnlockUploadError,
  newUnlockDraft,
  unlockDraftProblem,
  unlockToWizardData,
  type OfferType,
  type UnlockDraft,
  type UnlockProgress,
} from '../components/wizard/unlockDraft';
import RevenueGoalLine from '../components/Onboarding/RevenueGoalLine';
import { isFirstRunState, type FirstRunState } from '../components/Onboarding/firstRun';
import {
  newPartnerDraft,
  newSpotDraft,
  type InWhopDraft,
  type PartnerDraft,
  type SpotDraft,
  type WizardData,
} from '../components/wizard/wizardTypes';
import type { CreateExperimentRequest, ExperienceAttachResult, Placement } from '../types';
import { api, PlanLimitError } from '../services/api';
import { ApiRequestError } from '../services/apiErrors';
import { useCompanyId } from '../contexts/CompanyContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { isTestPath, pathTitle, slugify, spotById, spotForLabel, type TestPath } from '../utils/paths';
import { createPlacementWithCode } from '../utils/createPlacement';

type WizardStep = 1 | 2 | 3 | 4;
const STEP_LABELS = ['What to test', 'Place it', 'Go live'];

/** One line of progress. First run's own header already says where they are. */
function StepLine({ step }: { step: WizardStep }) {
  const current = Math.min(step, STEP_LABELS.length);
  return (
    <Text size="2" weight="medium" color="gray" aria-current="step">
      Step {current} of {STEP_LABELS.length} · {STEP_LABELS[current - 1]}
    </Text>
  );
}

const CHECKLIST: Record<TestPath, (where: string) => string[]> = {
  link: (where) => ['Link created', `Link posted in ${where}`, 'Someone other than you visits'],
  partner: (where) => [
    where === 'each spot' ? 'Links created' : 'Link created',
    where === 'each spot' ? 'Links sent to your partners' : `Link sent to ${where}`,
    'Someone other than you visits',
  ],
  in_whop: (where) => ['Test is live', `App added to ${where}`, 'First member visit'],
};

/**
 * Put the app on the free product: finds it if the seller already added it,
 * else creates and attaches an experience when the app has the permissions.
 * Anything that goes wrong means the seller adds it by hand.
 */
async function attachInWhop(
  experimentId: string,
  placement: Placement
): Promise<{ placement: Placement; attach: ExperienceAttachResult }> {
  try {
    return await api.linkPlacementExperience(experimentId, placement.id, true);
  } catch (err) {
    console.error('Could not add the app to the free product:', err);
    return { placement, attach: { status: 'manual', missingScope: 'unknown' } };
  }
}

function placementToSpotDraft(p: Placement): SpotDraft {
  const known = spotForLabel(p.label);
  return { key: p.id, spotId: known?.id ?? 'other', label: p.label, link: p.link };
}

function placementToPartnerDraft(p: Placement): PartnerDraft {
  return { key: p.id, label: p.label, affiliateCode: p.affiliateCode ?? '' };
}

export default function ExperimentWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  // First run's step 3; going back to the chooser keeps it first run.
  const firstRun = isFirstRunState(location.state);
  const chooserState: FirstRunState = { firstRun };
  // Which step this history entry is. Steps 1 and 2 are real history entries, so
  // browser back goes to step 1 with everything kept, not out of the wizard.
  const historyStep = (location.state as FirstRunState | null)?.wizardStep ?? 1;
  const [params, setParams] = useSearchParams();
  const rawPath = params.get('path');
  // Once the draft exists its id is in the URL (?id=), so a refresh, or
  // Home's "Finish setup", reopens it at the right step instead of a new one.
  const resumeId = params.get('id');
  const [resumedPath, setResumedPath] = useState<TestPath | null>(null);
  const path: TestPath = resumedPath ?? (isTestPath(rawPath) ? rawPath : 'link');
  const companyId = useCompanyId();
  const isMobile = useIsMobile();
  const { plan } = usePlan(companyId);

  const [step, setStep] = useState<WizardStep>(1);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);
  const [data, setData] = useState<WizardData>(() => ({
    companyId,
    productId: '',
    productName: '',
    basePlanId: '',
    name: '',
    currency: 'usd',
    variants: [
      { name: 'A', price: 999, interval: 'month', trialDays: 0 },
      { name: 'B', price: 1299, interval: 'month', trialDays: 0 },
    ],
    split: { A: 50, B: 50 },
  }));
  const updateData = useCallback((updates: Partial<WizardData>) => {
    setData((prev) => ({ ...prev, ...updates }));
  }, []);

  // Pay-to-unlock content (link and partner paths only).
  const [offerType, setOfferType] = useState<OfferType>('membership');
  const [unlockDraft, setUnlockDraft] = useState<UnlockDraft>(newUnlockDraft);
  const [unlockProgress, setUnlockProgress] = useState<UnlockProgress>({});
  const updateUnlockDraft = useCallback((updates: Partial<UnlockDraft>) => {
    setUnlockDraft((prev) => ({ ...prev, ...updates }));
  }, []);
  const isUnlock = offerType === 'unlock' && path !== 'in_whop';
  // Its draft exists, but the image didn't get through: only the image can still change.
  const unlockSaved = isUnlock && Boolean(unlockProgress.experimentId);
  // A reopened unlock draft has no form state left; its saved prices are in `data`.
  const [resumedUnlock, setResumedUnlock] = useState(false);
  // What the shared summary / go-live steps show.
  const testData = isUnlock && !resumedUnlock ? unlockToWizardData(unlockDraft, companyId) : data;

  const [spots, setSpots] = useState<SpotDraft[]>(() => [newSpotDraft('bio')]);
  const [currentSpotKey, setCurrentSpotKey] = useState(() => spots[0].key);
  const [partners, setPartners] = useState<PartnerDraft[]>(() => [newPartnerDraft()]);
  const [inWhop, setInWhop] = useState<InWhopDraft>({ freeProductId: '', freeProductName: '' });
  const [attach, setAttach] = useState<ExperienceAttachResult | null>(null);
  const updateInWhop = useCallback((updates: Partial<InWhopDraft>) => {
    setInWhop((prev) => ({ ...prev, ...updates }));
  }, []);

  const [experimentId, setExperimentId] = useState('');
  // Placements already created, by draft key, so a retry after a partial
  // failure doesn't create duplicates.
  const [created, setCreated] = useState<Record<string, Placement>>({});
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<Problem | null>(null);
  // Usernames Whop says don't exist, by partner draft key.
  const [unknownUsernames, setUnknownUsernames] = useState<Record<string, string>>({});
  // Step 1's product list actually loaded, for the product chosen.
  const [catalogReady, setCatalogReady] = useState(false);
  // Anything typed on the steps before a draft exists is only in this page.
  const [touched, setTouched] = useState(false);
  const [resumeAttempt, setResumeAttempt] = useState(0);
  // The server's 402 (the gate didn't catch it): creating is retried once Pro is active.
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [resume, setResume] = useState<'idle' | 'loading' | 'incomplete_unlock' | 'not_found' | 'failed'>(
    resumeId ? 'loading' : 'idle'
  );

  // Reopen a saved draft. Skipped when the id is the one this wizard just made.
  useEffect(() => {
    if (!resumeId || resumeId === experimentId) return;
    let cancelled = false;
    Promise.all([api.getExperiment(resumeId), api.getPlacements(resumeId)])
      .then(([experiment, saved]) => {
        if (cancelled) return;
        const plan = planResume(experiment, saved, rawPath);
        if (plan.kind === 'results') {
          navigate(`/experiments/${resumeId}`, { replace: true });
          return;
        }
        setExperimentId(resumeId);
        if (plan.kind === 'incomplete_unlock') {
          setResume('incomplete_unlock');
          return;
        }
        const { draft } = plan;
        setResumedPath(draft.path);
        setData(draft.data);
        setOfferType(draft.isUnlock ? 'unlock' : 'membership');
        setResumedUnlock(draft.isUnlock);
        setCreated(draft.created);
        setInWhop(draft.inWhop);
        // Back from Go live edits the spots that exist, so they fill the forms.
        const existingLinks = saved.filter((p) => p.kind === 'link');
        const existingPartners = saved.filter((p) => p.kind === 'partner');
        if (existingLinks.length > 0) {
          const drafts = existingLinks.map(placementToSpotDraft);
          setSpots(drafts);
          setCurrentSpotKey(drafts[0].key);
        }
        if (existingPartners.length > 0) setPartners(existingPartners.map(placementToPartnerDraft));
        setStep(draft.step);
        setResume('idle');
      })
      .catch((err: unknown) => {
        console.error('Failed to reopen the draft:', err);
        if (cancelled) return;
        // A failed load isn't a missing draft.
        const gone = err instanceof ApiRequestError && (err.status === 403 || err.status === 404);
        setResume(gone ? 'not_found' : 'failed');
      });
    return () => {
      cancelled = true;
    };
  }, [resumeId, experimentId, rawPath, navigate, resumeAttempt]);

  // Nothing is saved until step 2's Continue: warn before a reload loses it.
  const unsaved = !experimentId && step <= 2 && (step === 2 || touched);
  useEffect(() => {
    if (!unsaved) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsaved]);

  // Steps 1 and 2 follow history (Continue pushes an entry; Back pops it). Once
  // the draft exists history just records the step, for first run's header. A
  // reload starts at step 1 with nothing typed, so a leftover step means nothing.
  const pushedStep = useRef(false);
  const draftSaved = Boolean(experimentId);
  useEffect(() => {
    const state = (location.state ?? {}) as FirstRunState;
    const record = (wizardStep: number) =>
      navigate(
        { search: location.search },
        { replace: true, state: { ...state, wizardStep } }
      );
    if (pushedStep.current && !draftSaved) {
      if (historyStep === 1 && step === 2) setStep(1);
      else if (historyStep === 2 && step === 1) setStep(2);
    } else if (pushedStep.current && historyStep === 1) {
      // Browser back past a saved draft: it's on Home, nothing is lost.
      navigate('/', { replace: true });
    } else if (draftSaved && historyStep !== step) {
      record(step);
    } else if (!draftSaved && !resumeId && !pushedStep.current && historyStep !== 1) {
      record(1);
    }
    // Only history moving, or the step changing, means anything here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, step, draftSaved]);

  function goToPlaceStep() {
    if (pushedStep.current && historyStep === 2) return; // a double click
    pushedStep.current = true;
    navigate(
      { search: location.search },
      { state: { ...(location.state as FirstRunState | null), wizardStep: 2 } }
    );
  }

  function backToWhatToTest() {
    if (pushedStep.current) navigate(-1);
    else setStep(1);
  }

  /** Put the new draft's id in the URL (replace, keeping first-run state). */
  function rememberDraft(id: string) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('path', path);
        next.set('id', id);
        return next;
      },
      { replace: true, state: location.state }
    );
  }

  function leaveDiscardedDraft() {
    navigate('/', { replace: true });
  }

  const placements = Object.values(created);

  // The step-1 form shows an unlock draft's own problem; a membership test's goes beside Continue.
  const whatToTestProblem = isUnlock ? null : priceTestProblem(data);

  function whatToTestReady(): boolean {
    if (isUnlock) return unlockDraftProblem(unlockDraft) === null;
    return catalogReady && whatToTestProblem === null;
  }

  function placeReady(): boolean {
    if (path === 'link') {
      const labels = spots.map((s) => s.label.trim().toLowerCase());
      return labels.every(Boolean) && new Set(labels).size === labels.length;
    }
    if (path === 'partner') {
      const labels = partners.map((p) => p.label.trim().toLowerCase());
      return (
        labels.every(Boolean) &&
        new Set(labels).size === labels.length &&
        partners.every((p) => isValidUsername(p.affiliateCode))
      );
    }
    return Boolean(inWhop.freeProductId);
  }

  /**
   * Whop's word on the partners' usernames, before anything exists: a typo here
   * would otherwise cost the partner their credit. No answer from Whop isn't
   * a no, and a refusal when the checkout is made still surfaces afterwards.
   */
  async function usernamesWhopRefuses(): Promise<Record<string, string>> {
    const refused: Record<string, string> = {};
    if (path !== 'partner') return refused;
    for (const p of partners) {
      const username = cleanUsername(p.affiliateCode);
      if (!username || created[p.key]?.affiliateCode === username) continue;
      const exists = await api.checkUsername(companyId, username).catch(() => null);
      if (exists === false) refused[p.key] = username;
    }
    return refused;
  }

  async function createTestAndPlacements() {
    setCreating(true);
    setError(null);
    try {
      const refused = await usernamesWhopRefuses();
      setUnknownUsernames(refused);
      const names = Object.values(refused);
      if (names.length > 0) {
        // The rows may be off screen: this sits beside the button just pressed.
        setError({
          message: `Whop has no user called ${names.map((n) => `“${n}”`).join(' or ')}. Check the spelling, or clear the username: without one that partner gets no credit for sales.`,
        });
        return;
      }

      let id = experimentId;
      if (!id && isUnlock) {
        id = await createUnlockTest(companyId, unlockDraft, unlockProgress, setUnlockProgress);
        setExperimentId(id);
        rememberDraft(id);
      } else if (!id) {
        const request: CreateExperimentRequest = {
          companyId: data.companyId,
          productId: data.productId,
          productName: data.productName,
          name: data.name.trim(),
          currency: data.currency,
          variants: data.variants,
          split: data.split,
        };
        const experiment = await api.createExperiment(request);
        id = experiment._id;
        setExperimentId(id);
        rememberDraft(id);
      }

      const made: Record<string, Placement> = { ...created };
      if (path === 'link') {
        for (const s of spots) {
          const label = s.label.trim();
          const existing = made[s.key];
          if (existing) {
            if (existing.label !== label) {
              made[s.key] = await api.updatePlacement(id, existing.id, { label });
              setCreated({ ...made });
            }
            continue;
          }
          const code = s.spotId === 'other' ? slugify(label) || 'link' : spotById(s.spotId).code;
          made[s.key] = await createPlacementWithCode(id, { kind: 'link', label }, code);
          setCreated({ ...made });
        }
      } else if (path === 'partner') {
        for (const p of partners) {
          const label = p.label.trim();
          const username = cleanUsername(p.affiliateCode);
          const existing = made[p.key];
          if (existing) {
            const usernameChanged = (existing.affiliateCode ?? '') !== username;
            if (existing.label !== label || usernameChanged) {
              made[p.key] = await api.updatePlacement(id, existing.id, {
                ...(existing.label !== label && { label }),
                ...(usernameChanged && { affiliateCode: username }),
              });
              setCreated({ ...made });
            }
            continue;
          }
          const slug = slugify(label);
          made[p.key] = await createPlacementWithCode(
            id,
            {
              kind: 'partner',
              label,
              ...(slug && { source: slug }),
              ...(username && { affiliateCode: username }),
            },
            slug || undefined
          );
          setCreated({ ...made });
        }
      } else {
        // Changing free product on the way Back: the new spot first, then the old one.
        const previous = made[IN_WHOP_KEY];
        const productChanged = previous && previous.productId !== inWhop.freeProductId;
        if (!previous || productChanged) {
          made[IN_WHOP_KEY] = await api.createPlacement(id, {
            kind: 'in_whop',
            productId: inWhop.freeProductId,
            label: inWhop.freeProductName,
          });
          setCreated({ ...made });
          if (productChanged) await api.removePlacement(id, previous.id);
        }
        const linked = await attachInWhop(id, made[IN_WHOP_KEY]);
        made[IN_WHOP_KEY] = linked.placement;
        setCreated({ ...made });
        setAttach(linked.attach);
      }

      // Spots taken out on the way Back from Go live. New ones are in by now,
      // so the test is never left with none.
      if (path !== 'in_whop') {
        const kept = new Set((path === 'link' ? spots : partners).map((d) => d.key));
        for (const [key, placement] of Object.entries(made)) {
          if (kept.has(key)) continue;
          await api.removePlacement(id, placement.id);
          delete made[key];
          setCreated({ ...made });
        }
      }

      // The form now shows the real link, for another Back.
      if (path === 'link') {
        setSpots((prev) => prev.map((s) => ({ ...s, link: made[s.key]?.link ?? s.link })));
      }
      setStep(3);
    } catch (err) {
      if (err instanceof PlanLimitError) {
        setShowUpgrade(true);
        return;
      }
      setError({
        message:
          err instanceof UnlockUploadError
            ? err.message
            : `Couldn't save your test. ${(err as Error).message || 'Try again.'}`,
        blockingTest: err instanceof ApiRequestError ? err.blockingTest : undefined,
      });
    } finally {
      setCreating(false);
    }
  }

  const locked = Boolean(experimentId); // steps 1–2 can't change once created

  if (resume === 'loading') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
        <Spinner size="3" aria-label="Opening your draft" />
      </div>
    );
  }

  if (resume === 'failed') {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <LoadFailed
          what="your draft"
          onRetry={() => {
            setResume('loading');
            setResumeAttempt((n) => n + 1);
          }}
        />
      </div>
    );
  }

  if (resume === 'not_found' || resume === 'incomplete_unlock') {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <Card size={isMobile ? '2' : '3'} className="vara2-step-in">
          <Heading size="5" style={{ marginBottom: 'var(--space-2)' }}>
            {resume === 'not_found' ? "We couldn't find that draft" : "This draft didn't finish saving"}
          </Heading>
          <Text size="2" color="gray" style={{ display: 'block', marginBottom: 'var(--space-4)' }}>
            {resume === 'not_found'
              ? 'It may have been deleted, or it belongs to another business.'
              : 'Its image never finished uploading, so it can’t go live. Delete it and set the offer up again.'}
          </Text>
          <div className="vara2-button-stack" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', alignItems: 'center' }}>
            <Button size="3" variant="solid" onClick={() => navigate('/new', { state: chooserState })}>
              Start a new test
            </Button>
            {resume === 'incomplete_unlock' && experimentId && (
              <DeleteDraftButton experimentId={experimentId} isUnlock onDiscarded={leaveDiscardedDraft} />
            )}
          </div>
        </Card>
      </div>
    );
  }
  const where =
    path === 'in_whop'
      ? inWhop.freeProductName || 'your free product'
      : path === 'partner'
        ? partners.length === 1
          ? partners[0].label.trim() || 'your partner'
          : 'each spot'
        : spots.length === 1
          ? spots[0].label || 'your spot'
          : 'each spot';

  const summaryColumn = step === 2 && (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', minWidth: 0 }}>
      {path === 'in_whop' && (
        <MemberOfferPreview data={data} freeProductName={inWhop.freeProductName} />
      )}
      <TestSummaryAside
        data={testData}
        checklist={CHECKLIST[path](where)}
        isUnlock={isUnlock}
        inWhop={path === 'in_whop'}
      />
    </div>
  );

  return (
    <div style={{ maxWidth: step === 2 ? 1040 : 760, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <Button
          variant="ghost"
          color="gray"
          size="2"
          style={{ minHeight: 44 }}
          onClick={() =>
            locked
              ? navigate('/')
              : step === 2
                ? backToWhatToTest()
                : navigate('/new', { state: chooserState })
          }
        >
          <ArrowLeft16 />
          {locked ? 'Your price tests' : step === 2 ? STEP_LABELS[0] : pathTitle(path)}
        </Button>
        {/* First run's own header says where they are, until the draft is saved. */}
        {(!firstRun || step === 3) && step <= 3 && <StepLine step={step} />}
      </div>
      {needsProForNewTest(plan) && step <= 3 && (
        <Text size="2" color="gray">
          You've used your free test, so going live with this one needs Pro.
        </Text>
      )}
      {firstRun && step === 1 && <RevenueGoalLine />}

      {step === 1 && (
        <Card size={isMobile ? '2' : '3'} className="vara2-step-in">
          <div onChangeCapture={() => setTouched(true)}>
          {path !== 'in_whop' && !unlockSaved && (
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <OfferTypeChoice value={offerType} onChange={setOfferType} />
            </div>
          )}
          {isUnlock ? (
            <StepWhatToSell draft={unlockDraft} onChange={updateUnlockDraft} saved={unlockSaved} />
          ) : (
            <StepWhatToTest
              data={data}
              onChange={updateData}
              promosAllowed={path !== 'in_whop'}
              initialProductId={params.get('product')}
              onCatalogReady={setCatalogReady}
            />
          )}
          </div>
        </Card>
      )}

      {step === 2 && (
        <div
          className="vara2-step-in"
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) 320px',
            gap: 'var(--space-5)',
            alignItems: 'start',
          }}
        >
          <div style={{ minWidth: 0 }}>
            {path === 'link' && (
              <StepPlaceLink
                spots={spots}
                currentKey={currentSpotKey}
                isUnlock={isUnlock}
                onChange={(next, key) => {
                  setSpots(next);
                  setCurrentSpotKey(key);
                }}
              />
            )}
            {path === 'partner' && (
              <StepPlacePartners
                partners={partners}
                onChange={(next) => {
                  setPartners(next);
                  setError(null);
                }}
                unknownUsernames={unknownUsernames}
              />
            )}
            {path === 'in_whop' && (
              <StepPlaceInWhop
                data={data}
                inWhop={inWhop}
                onChange={updateInWhop}
                onUseLink={
                  locked
                    ? undefined
                    : () =>
                        navigate('/experiments/new?path=link', { replace: true, state: location.state })
                }
              />
            )}
          </div>
          {!isMobile && summaryColumn}
        </div>
      )}

      {step === 3 && experimentId && (
        <Card size={isMobile ? '2' : '3'} className="vara2-step-in">
          <StepGoLive
            experimentId={experimentId}
            data={testData}
            path={path}
            placements={placements}
            freeProductName={inWhop.freeProductName}
            attach={attach}
            isUnlock={isUnlock}
            onBack={() => setStep(2)}
            onLive={() => setStep(4)}
          />
        </Card>
      )}
      {step === 3 && experimentId && (
        <div>
          <DeleteDraftButton experimentId={experimentId} isUnlock={isUnlock} onDiscarded={leaveDiscardedDraft} />
        </div>
      )}

      {step === 4 && experimentId && (
        <div className="vara2-step-in" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          <Card size={isMobile ? '2' : '3'}>
            <FirstVisitChecklist
              experimentId={experimentId}
              placements={placements}
              freeProductName={inWhop.freeProductName}
              attach={attach}
            />
          </Card>
          <div className="vara2-button-stack" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
            <Button size="3" variant="solid" style={{ flex: '1 1 200px' }} onClick={() => navigate(`/experiments/${experimentId}`)}>
              See results
            </Button>
            <Button size="3" variant="soft" color="gray" style={{ flex: '1 1 200px' }} onClick={() => navigate('/')}>
              Back to your tests
            </Button>
          </div>
        </div>
      )}

      {error && <ProblemCallout problem={error} />}

      {step === 2 && locked && (
        <Text size="2" color="gray">
          Prices are set once a draft is saved. To change them, delete this draft and start again.
        </Text>
      )}

      {/* Navigation */}
      {step <= 2 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-3)' }}>
          {locked ? (
            <DeleteDraftButton experimentId={experimentId} isUnlock={isUnlock} onDiscarded={leaveDiscardedDraft} />
          ) : step === 1 && unlockSaved && unlockProgress.experimentId ? (
            <DeleteDraftButton experimentId={unlockProgress.experimentId} isUnlock onDiscarded={leaveDiscardedDraft} />
          ) : (
            <Button
              variant="soft"
              color="gray"
              size="3"
              onClick={() => (step === 1 ? navigate('/new', { state: chooserState }) : backToWhatToTest())}
              disabled={creating}
            >
              <ArrowLeft16 />
              Back
            </Button>
          )}
          {step === 1 ? (
            <Button variant="solid" size="3" onClick={goToPlaceStep} disabled={!whatToTestReady()}>
              Continue
              <ArrowRight16 />
            </Button>
          ) : (
            <Button
              variant="solid"
              size="3"
              onClick={createTestAndPlacements}
              disabled={!placeReady() || creating}
              loading={creating}
            >
              Continue to go live
              <ArrowRight16 />
            </Button>
          )}
        </div>
      )}
      {step === 1 && whatToTestProblem && catalogReady && (
        <Text size="2" color="gray" style={{ textAlign: 'right' }}>
          {whatToTestProblem}
        </Text>
      )}
      {step === 2 && !placeReady() && (
        <Text size="2" color="gray" style={{ textAlign: 'right' }}>
          {path === 'in_whop'
            ? 'Pick the free product your members are in to continue.'
            : path === 'partner' && !partners.every((p) => isValidUsername(p.affiliateCode))
              ? `Fix the username on ${partners.filter((p) => !isValidUsername(p.affiliateCode)).map((p) => `link ${partners.indexOf(p) + 1}`).join(' and ')} to continue. ${USERNAME_RULE}`
            : `Name every ${path === 'partner' ? 'link' : 'spot'} (no repeats) to continue.`}
        </Text>
      )}
      {/* On a phone the summary comes after Continue, so the action isn't buried. */}
      {isMobile && summaryColumn}

      {showUpgrade && (
        <UpgradeDialog
          companyId={companyId}
          onClose={() => setShowUpgrade(false)}
          onUpgraded={() => {
            setShowUpgrade(false);
            createTestAndPlacements();
          }}
        />
      )}
    </div>
  );
}
